import React, { useEffect, useRef, useState } from 'react';
import { compose } from 'redux';
import { connect, useDispatch } from 'react-redux';
import { goBack, goForward } from 'connected-react-router';
import IdleTimer from 'react-idle-timer';
import { ApplicationFormShell } from 'web-ui-components/lib/layout/shells';
import { ContainedHeader } from 'web-ui-components/lib/layout/headers';
import { ShellInnerSpacer } from 'web-ui-components/lib/layout/shells';
import { Loader } from 'web-ui-components/lib/organisms/modals';
import { navigateToScreen, redirectToOriginApplication } from 'actionCreators/navigation';
import { updateQueryParam, setQueryParam } from 'actionCreators/config';
import { responseFailed } from 'actionCreators/error';
import { withSaveResume } from 'hocs/withSaveResume';
import { useAppStart } from 'hocs/withAppStart';
import { CustomerInformationProvider } from 'libs/CustomerInformation';
import { withDeviceContext } from 'context/DeviceContext';
import { JourneyProvider } from 'context/JourneyContext';
import { GlobalErrorBoundary } from 'components/screens/GlobalErrorBoundary';
import { UtilityNavigation } from 'components/UtilityNavigation';
import { NTB_JOURNEY, THIRTY_MINUTES_IN_MS } from 'constants/general';
import { deleteToken } from 'actionCreators/token';
import {
  DIRECT_DEBIT_INSTRUCTIONS,
  INCOME_AND_EXPENSES,
  REVIEW_YOUR_DETAILS,
  YOUR_ADDRESS_DETAILS,
  YOUR_CONTACT_DETAILS,
  YOUR_EMPLOYMENT_DETAILS,
  YOUR_PERSONAL_DETAILS
} from 'constants/paths';
import { AppRouter, customerInformationPaths } from './routes';
import SaveExitScreen from './components/screens/SaveExit';
import { shouldHideBackButton } from 'utils/shouldHideBackButton';

import './App.css';
import { useSelector } from 'react-redux';
import { setContentSquare } from 'utils/setContentSquare';
import { ProgressSteps } from 'components/ProgressSteps/ProgressSteps';
import { ProgressBar } from 'web-ui-components/lib/navigation/indicators';
import {
  existingJourneySteps,
  existingProgressPages,
  ntbJourneySteps,
  ntbProgressPages
} from 'utils/progressSteps';

function App({
  isLoading,
  config,
  error: { isError },
  parsedParams,
  location,
  action,
  exitModalOpen,
  hasDIPCheck,
  hasOffer,
  resumedCustomerData,
  resumedCaseDetails,
  cicValue
}) {
  const hasUserTimedOut = useSelector((state) => state.userSession?.hasTimedOut);
  const timerRef = useRef(null);
  const dispatch = useDispatch();
  const isNative = config.nativeAppData ? config.nativeAppData.isNative : false;
  const [resetCustomerInfo, setResetCustomerInfo] = useState(false);
  const [width, setWidth] = useState(window.innerWidth);

  const handleWindowSizeChange = () => setWidth(window.innerWidth);

  useEffect(() => {
    window.addEventListener('resize', handleWindowSizeChange);
    return () => {
      window.removeEventListener('resize', handleWindowSizeChange);
    };
  }, []);

  /* Keeping the below code as waiting design for new components */
  // const isMobile = width <= 768;

  // const displayProgressSteps = () => {
  //   if (config.journey && !hasDIPCheck && !hasUserTimedOut) {
  //     return config.journey === NTB_JOURNEY
  //       ? ntbProgressPages.includes(location?.pathname)
  //       : existingProgressPages.includes(location?.pathname);
  //   }
  //   return false;
  // };

  const injectDynamicScript = (scriptSrc, domElementId, callback = false) => {
    const existingScript = document.getElementById(domElementId);

    if (!existingScript) {
      const script = document.createElement('script');
      script.src = scriptSrc;
      script.id = domElementId;
      document.getElementsByTagName('head')[0].appendChild(script);

      script.onload = () => {
        if (callback) return callback();
      };
    }

    if (existingScript && callback) callback();
  };

  config.enableContentSquare &&
    injectDynamicScript(config.contentSquareUrl, 'contentSquareScript', setContentSquare);

  useEffect(() => {
    dispatch(updateQueryParam(parsedParams));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useAppStart();

  useEffect(() => {
    if (isError) {
      timerRef.current.pause();
    }
  }, [isError]);

  return (
    <IdleTimer
      timeout={THIRTY_MINUTES_IN_MS}
      ref={timerRef}
      onIdle={() => {
        dispatch(deleteToken(timerRef));
        setResetCustomerInfo(true);
      }}
    >
      <ApplicationFormShell
        header={
          !isNative && (
            <ContainedHeader
              subline={
                config.loanType === 'resume'
                  ? 'Apply for a personal loan'
                  : hasDIPCheck
                  ? 'Apply for a personal loan'
                  : 'Check your eligibility for a personal loan'
              }
            />
          )
        }
        utilityNavigation={!isNative && <UtilityNavigation location={location} />}
        /* Use RightSidebarPortal in a screen to display content in Right Sidebar */
        rightSidebar={<aside id="vm_rightsidebar_portal"></aside>}
      >
        <GlobalErrorBoundary
          isError={isError}
          onReturnHome={() => dispatch(redirectToOriginApplication())}
          isNative={isNative}
          hasUserTimedOut={hasUserTimedOut}
        >
          <JourneyProvider value={config.journey}>
            <CustomerInformationProvider
              bankId={config.bankId}
              journey={config.journey}
              customerId={config.customerId}
              isResume={config.loanType === 'resume'}
              paasApiBaseUrl={config.configUrls.paasApiBaseUrl}
              libertyApiBaseiUrl={config.configUrls.libertyApiBaseiUrl}
              token={config.accessToken}
              isSecondaryApplicant={config.isSecondaryApplicant}
              userTrackingId={config.userTrackingId}
              globalErrorAction={(payload) => dispatch(responseFailed(payload))}
              goToPath={(path, options) => dispatch(navigateToScreen(path, options, true))}
              screenPaths={customerInformationPaths}
              toggleBackButton={(inEditMode) => dispatch(setQueryParam({ inEditMode }))}
              editablePaths={[
                REVIEW_YOUR_DETAILS,
                INCOME_AND_EXPENSES,
                YOUR_EMPLOYMENT_DETAILS,
                YOUR_CONTACT_DETAILS,
                YOUR_ADDRESS_DETAILS,
                YOUR_PERSONAL_DETAILS
              ]}
              cdnUrl={config.configUrls.personalLoansUrl}
              resumedCustomerData={resumedCustomerData}
              brandId={config.brandId}
              authenticationUrl={config.configUrls.authenticationUrl}
              sourceId={config.sourceId}
              resumedCaseDetails={resumedCaseDetails}
              cicValue={cicValue}
              resetCustomerInfo={resetCustomerInfo}
              setResetCustomerInfo={setResetCustomerInfo}
            >
              <ShellInnerSpacer>
                <>
                  {exitModalOpen && <SaveExitScreen />}
                  <div style={{ display: exitModalOpen ? 'none' : 'block' }}>
                    <AppRouter
                      location={location}
                      action={action}
                      lockNavigation={exitModalOpen}
                      goForward={() => dispatch(goForward())}
                      goBack={() => dispatch(goBack())}
                    />
                  </div>
                </>
              </ShellInnerSpacer>
            </CustomerInformationProvider>
          </JourneyProvider>
          <Loader
            isOpen={isLoading}
            title={
              location.pathname === DIRECT_DEBIT_INSTRUCTIONS ? `Stay right there` : `Just a sec`
            }
            body={
              location.pathname === DIRECT_DEBIT_INSTRUCTIONS
                ? `We’re doing our thing. This could take up to 30 seconds.`
                : `We’re doing our thing – don’t go anywhere`
            }
          />
        </GlobalErrorBoundary>
      </ApplicationFormShell>
    </IdleTimer>
  );
}

const ComposedApp = compose(
  withSaveResume(),
  withDeviceContext,
  connect((state) => ({
    isLoading: state.loader.isLoading,
    error: state.error,
    config: state.config,
    location: state.router.location,
    action: state.router.action,
    hideBackButton: shouldHideBackButton(
      state.exitModalOpen,
      state.config,
      state.router.location.pathname,
      state.navigation
    ),
    exitModalOpen: state.exitModalOpen,
    hasDIPCheck: state.caseDetails.dipCaseInfo,
    hasOffer: state.offer.offerInfo,
    resumedCustomerData: state.customer.customerInfo,
    resumedCaseDetails: state?.caseDetails,
    cicValue: state.customer.financialCircumstanceValue
  }))
)(App);

export { ComposedApp as App };
